import { useState, useEffect, useMemo, useCallback } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import { isEqual } from 'lodash';
import {
  InputCreateReminderRule,
  InputUpdateReminderRule,
  ReminderRule,
} from 'graphql-query-contracts';
import {
  createReminderRuleMutation,
  deleteReminderRuleMutation,
  updateReminderRuleMutation,
  getAllReminderRulesQuery,
} from 'graphql-queries';
import { Workflow } from 'application-types';
import {
  convertReminderRuleToWorkflow,
  convertWorkflowToCreateReminderRuleInput,
  convertWorkflowToUpdateReminderRuleInput,
} from './mappers';

export const useReminderRuleWorkflows = (channelId?: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);

  const [createReminderRule, { loading: loadingCreateReminderRule }] =
    useMutation(createReminderRuleMutation);

  const [updateReminderRule, { loading: loadingUpdateReminderRule }] =
    useMutation(updateReminderRuleMutation);

  const [deleteReminderRule, { loading: loadingDeleteReminderRule }] =
    useMutation(deleteReminderRuleMutation);

  const [getAllReminderRules, { data, loading: loadingReminderRules }] =
    useLazyQuery(getAllReminderRulesQuery, {
      fetchPolicy: 'network-only',
    });

  const reminderRulesData: ReminderRule[] = useMemo(
    () => data?.getAllReminderRules || [],
    [data]
  );

  const workflowsFromServer = reminderRulesData.map(
    (reminderRule: ReminderRule) =>
      convertReminderRuleToWorkflow(reminderRule, channelId || '')
  );

  const fetchWorkflows = async () => {
    if (channelId) {
      setIsLoading(true);

      try {
        const response = await getAllReminderRules({
          variables: {
            channelId,
          },
          fetchPolicy: 'network-only',
        });

        return response?.data?.getAllReminderRules || [];
      } catch (error) {
        console.error('Failed to fetch reminder rule workflows:', error);

        return [];
      } finally {
        setIsLoading(false);
      }
    }

    return [];
  };

  useEffect(() => {
    setWorkflows(workflowsFromServer);
  }, [JSON.stringify(workflowsFromServer)]);

  useEffect(() => {
    if (channelId) {
      fetchWorkflows();
    }
  }, [channelId]);

  const handleCreateReminderRule = async (
    reminderRule: InputCreateReminderRule
  ) => {
    await createReminderRule({
      variables: {
        reminderRule,
      },
    });
  };

  const handleUpdateReminderRule = async (
    reminderRule: InputUpdateReminderRule
  ) => {
    await updateReminderRule({
      variables: {
        reminderRule,
      },
    });
  };

  const handleDeleteReminderRule = async (id: string) => {
    await deleteReminderRule({
      variables: {
        id,
      },
    });
  };

  const onAddWorkflow = async (workflowToAdd: Workflow) => {
    setWorkflows(prevWorkflows => [...prevWorkflows, workflowToAdd]);

    return workflowToAdd;
  };

  const onRemoveWorkflow = async (workflowId: string) => {
    const existsOnServer = workflowsFromServer.some(
      workflow => workflow._id === workflowId
    );

    if (existsOnServer) {
      await handleDeleteReminderRule(workflowId);
    }

    setWorkflows(prevWorkflows =>
      prevWorkflows.filter(workflow => workflow._id !== workflowId)
    );
  };

  const buildSavePromises = useCallback(
    (
      allWorkflows: Workflow[],
      workflowsFromServer: Workflow[]
    ): Promise<any>[] => {
      const promises: Promise<any>[] = [];

      for (const workflow of allWorkflows) {
        const prevWorkflow = workflowsFromServer.find(
          prevWorkflow => prevWorkflow._id === workflow._id
        );

        if (!prevWorkflow) {
          promises.push(
            handleCreateReminderRule(
              convertWorkflowToCreateReminderRuleInput(workflow, channelId)
            )
          );
        } else {
          if (isEqual(prevWorkflow, workflow)) continue;

          promises.push(
            handleUpdateReminderRule(
              convertWorkflowToUpdateReminderRuleInput(workflow)
            )
          );
        }
      }

      return promises;
    },
    []
  );

  const onWorkflowsSave = async (allWorkflows: Workflow[]) => {
    try {
      setIsLoading(true);

      if (isEqual(allWorkflows, workflowsFromServer)) {
        return true;
      }

      const promises = buildSavePromises(allWorkflows, workflowsFromServer);
      const results = await Promise.allSettled(promises);
      const failures = results.filter(
        (r): r is PromiseRejectedResult => r.status === 'rejected'
      );

      if (failures.length > 0) {
        return false;
      }

      await fetchWorkflows();

      return true;
    } catch (error) {
      console.error(error);

      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    loading:
      isLoading ||
      loadingCreateReminderRule ||
      loadingUpdateReminderRule ||
      loadingDeleteReminderRule ||
      loadingReminderRules,
    reminderRuleWorkflows: workflows,
    addReminderRuleWorkflow: onAddWorkflow,
    removeReminderRuleWorkflow: onRemoveWorkflow,
    saveReminderRuleWorkflows: onWorkflowsSave,
  };
};
